// Medición del quiz. Módulo aparte a propósito: quiz.js emite eventos del DOM
// y no sabe nada de analítica; este archivo los traduce a dataLayer. Si mañana
// cambia la herramienta, se cambia este archivo y el motor no se toca.
//
// POR QUÉ SOLO dataLayer, y no fbq ni gtag directos:
// la tienda ya carga GTM (GTM-NWPC2QT4) y Shopify Web Pixels. Disparar píxeles
// desde aquí duplicaría eventos que GTM ya enruta y rompería el consentimiento
// que GTM administra. AGENTS.md lo pide explícitamente.
//
// CONVENCIÓN: los eventos propios de la tienda usan el prefijo afh_
// (afh_newsletter_signup, afh_contact_form_submit...). El quiz lo respeta:
// afh_quiz_view, afh_quiz_answer, etc. Catálogo completo y cómo consultarlo:
// docs/quiz/MEDICION.md.
//
// SIN DUPLICAR EL COMERCIO ESTÁNDAR: el carrito y la compra ya los miden
// Shopify Web Pixels (product_added_to_cart, checkout_started,
// checkout_completed). Por eso solo afh_quiz_result_view lleva el objeto
// «ecommerce» (para públicos con producto); el resto manda los productos en
// quiz_items, que ninguna etiqueta de ecommerce lee por defecto.
//
// SIN DATOS PERSONALES: el correo y el nombre NUNCA se empujan al dataLayer.

const PREFIJO = 'afh_';

const dl = () => {
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
};

// DESDE EL 2026-10-17 NADIE LEE EL dataLayer: se retiró GTM y GA4 vive dentro del
// píxel de la app de Google, que solo escucha los eventos estándar de Shopify. Los
// eventos del quiz viajan además a GA4 por el Worker de la app
// (app/src/lib/quiz-events.ts), con el cliente y la sesión de las cookies de GA4,
// los mismos que pdp-order-ids.js sella en el carrito. Solo con consentimiento de
// analítica, como el píxel de OpenAI, y sin objetos: ni ecommerce ni quiz_items.
const GA4_PROXY = '/apps/suscripciones/quiz-events';
const GA4_MAX = 25;
const cola = [];
let temporizador = null;

const cookie = (nombre) => document.cookie.match(new RegExp(`(?:^|; )${nombre}=([^;]*)`))?.[1] ?? '';
const conConsentimiento = () => Boolean(window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.());

function enviarGa4() {
  clearTimeout(temporizador);
  if (!cola.length) return;
  const events = cola.splice(0, cola.length);
  const clientId = cookie('_ga').match(/^GA1\.\d+\.(\d+\.\d+)$/)?.[1];
  if (!clientId || !conConsentimiento()) return;
  const sessionId = cookie('_ga_N2SPNZT2WD').match(/^GS\d\.\d+\.s?(\d+)/)?.[1];
  const cuerpo = JSON.stringify({ client_id: clientId, session_id: sessionId, events });
  // sendBeacon sobrevive a la navegación al carrito o al checkout que sigue al resultado.
  if (navigator.sendBeacon?.(GA4_PROXY, new Blob([cuerpo], { type: 'application/json' }))) return;
  fetch(GA4_PROXY, { method: 'POST', keepalive: true, headers: { 'Content-Type': 'application/json' }, body: cuerpo }).catch(() => {});
}

function aGa4(nombre, datos) {
  const params = {};
  for (const [clave, valor] of Object.entries(datos)) {
    if (valor !== undefined && valor !== null && typeof valor !== 'object') params[clave] = valor;
  }
  cola.push({ name: PREFIJO + nombre, params });
  // Junta las respuestas seguidas en un solo envío.
  if (cola.length >= GA4_MAX) return enviarGa4();
  clearTimeout(temporizador);
  temporizador = setTimeout(enviarGa4, 2000);
}

window.addEventListener('pagehide', enviarGa4);

function empujar(nombre, datos) {
  dl().push({ event: PREFIJO + nombre, ...datos });
  aGa4(nombre, datos);
}

// Para la invitación de un artículo (quiz-entrada.js), que mide sin motor. En
// window y no como export: tools/quiz-vista-previa.mjs carga este archivo como
// script clásico.
window.quizMedir = empujar;

// Artículo del blog desde el que se entró: lo trae la ventana del artículo en
// data-quiz-articulo, o la URL (?articulo=) si la ventana falló y se navegó a
// la página del quiz. Va como quiz_articulo en cada evento, para medir
// blog → quiz → compra. Sin artículo, los eventos no cambian.
const HANDLE = /^[a-z0-9-]{1,100}$/;
let articuloUrl = '';
try {
  articuloUrl = new URLSearchParams(window.location.search).get('articulo') || '';
} catch {
  /* sin URL: no hay artículo de origen */
}
const articuloDe = (el) => {
  const h = el?.dataset?.quizArticulo || (el?.closest?.('[data-quiz]') ? articuloUrl : '');
  return HANDLE.test(h || '') ? h : undefined;
};

// Mismo id que usa el remarketing dinámico de la tienda.
const itemId = (handle) => `shopify_${handle}`;
const pesos = (centavos) => (centavos || 0) / 100;

function item(producto, indice = 0, cantidad = 1) {
  if (!producto) return null;
  return {
    index: indice,
    item_id: itemId(producto.handle),
    item_name: producto.titulo,
    price: pesos(producto.precio),
    quantity: cantidad,
  };
}

// Cada evento del DOM → nombre en dataLayer y campos. quiz_id va siempre.
const EVENTOS = {
  open: ['quiz_open', (d) => ({ quiz_origen: d.desde })],
  view: ['quiz_view', (d) => ({ quiz_origen: d.origen, quiz_desde: d.desde ? `shopify_${d.desde}` : undefined })],
  start: ['quiz_start', (d) => ({ quiz_retomado: Boolean(d.retomado) })],
  step: [
    'quiz_answer',
    (d) => ({ question_id: d.pregunta, answer_id: d.opcion, step_number: d.paso, step_total: d.total }),
  ],
  why_open: ['quiz_why_open', (d) => ({ question_id: d.pregunta })],
  // Beneficio desbloqueado por avanzar (hoy ningún quiz tiene: vuelve con la guía).
  gift: ['quiz_gift', (d) => ({ quiz_regalo: d.regalo })],
  complete: ['quiz_complete', (d) => ({ quiz_respondidas: d.respondidas })],
  email_view: ['quiz_email_view', (d) => ({ quiz_email_obligatorio: d.obligatoria })],
  // El lead SIN datos personales: solo que ocurrió.
  lead: ['quiz_email_submit', () => ({ quiz_consentimiento: true })],
  email_skip: ['quiz_email_skip', () => ({})],
  analysis_view: ['quiz_analysis_view', () => ({})],
  result: [
    'quiz_result_view',
    (d) => ({
      product_id: itemId(d.handle),
      result_key: d.handle,
      recommendation_reason: d.motivo,
      quiz_addons: (d.complementos || []).map(itemId).join(','),
      quiz_formato_no_disponible: d.formato_no_disponible,
      ecommerce: d.producto
        ? { currency: d.moneda || 'COP', value: pesos(d.producto.precio), items: [item(d.producto)] }
        : undefined,
    }),
  ],
  no_result: ['quiz_no_result', (d) => ({ quiz_motivo: d.motivo })],
  purchase_type: ['quiz_purchase_type_select', (d) => ({ product_id: itemId(d.handle), purchase_type: d.tipo })],
  addon_click: [
    'quiz_addon_click',
    (d) => ({ product_id: itemId(d.handle), result_key: d.resultado, quiz_accion: d.accion }),
  ],
  primary_click: [
    'quiz_primary_product_click',
    (d) => ({
      product_id: itemId(d.handle),
      quiz_accion: d.accion,
      purchase_type: d.tipo,
      quiz_cantidad: d.cantidad,
      quiz_valor: pesos(d.valor),
    }),
  ],
  add_to_cart: [
    'quiz_add_to_cart',
    (d) => ({
      product_id: itemId(d.handle),
      purchase_type: 'one_time',
      quiz_valor: pesos(d.valor),
      quiz_con_addons: (d.complementos || []).length > 0,
      quiz_items: (d.items || []).map((x, i) => ({ ...item(x, i, x.cantidad), item_category: x.rol })),
    }),
  ],
  // La suscripción se cobra en Treli, fuera de Shopify: este es el último
  // punto que el navegador ve. La compra efectiva se concilia con Treli.
  subscription_start: [
    'quiz_subscription_start',
    (d) => ({ product_id: itemId(d.handle), purchase_type: 'subscription', quiz_valor: pesos(d.valor) }),
  ],
  abandon: [
    'quiz_abandon',
    (d) => ({ step_number: d.paso, question_id: d.pregunta, quiz_respondidas: d.respondidas }),
  ],
};

Object.entries(EVENTOS).forEach(([dom, [nombre, campos]]) => {
  document.addEventListener(`quiz:${dom}`, (e) => {
    const articulo = articuloDe(e.target);
    empujar(nombre, { quiz_id: e.detail.id, ...campos(e.detail), ...(articulo && { quiz_articulo: articulo }) });
  });
});
